import { Controller } from "@hotwired/stimulus"

// Bottom-drawer player for the onboarding reveal (docs/features/v1/onboarding.md
// — "Moment of Magic"). Each pick's thumbnail carries a <template> holding the
// same segment-player used inline elsewhere; tapping the thumbnail clones that
// template into a slide-up sheet and starts the clip, instead of playing inline.
//
// This mirrors video_popover_controller's proven open/close/back-button/teardown
// behaviour, but the content is a cloned local template (the reveal is anonymous,
// so there is no /watch/ URL to fetch — the clip plays via segment-player preview
// mode, which autoplays itself on connect). Full-screen slide-up on mobile; on
// desktop the panel rises into a centered card over a dim backdrop (see the
// sheet/panel classes). The clip's title + channel are shown in the top bar.
export default class extends Controller {
  static targets = ["sheet", "panel", "content", "template", "title", "channel"]
  static values = { open: { type: Boolean, default: false } }

  connect() {
    this.boundPopState = this.handlePopState.bind(this)
    this.boundBeforeCache = this.forceClose.bind(this)
    window.addEventListener("popstate", this.boundPopState)
    // Force the sheet shut before Turbo snapshots the page, so a restored DOM
    // can never bring back a visible, click-eating overlay (same reasoning as
    // video_popover_controller).
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
  }

  disconnect() {
    window.removeEventListener("popstate", this.boundPopState)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    if (this.hideTimeout) clearTimeout(this.hideTimeout)
    document.body.style.overflow = ""
  }

  // Thumbnail tapped: clone that pick's player template into the sheet and slide
  // up. The cloned segment-player auto-initialises in preview mode on connect and
  // autoplays itself (see segment_player_controller) — no handshake needed. The
  // `index`/`title`/`channel` params come from the trigger button's data attrs.
  open(event) {
    if (event) event.preventDefault()
    const template = this.templateTargets[event.params.index]
    if (!template) return

    this.contentTarget.replaceChildren(template.content.cloneNode(true))
    this.setHeader(event.params.title, event.params.channel)

    this.showSheet()
    this.pushHistoryEntry()
  }

  // Populate the top-bar title + channel; hide the channel line when absent so it
  // doesn't leave an empty gap.
  setHeader(title, channel) {
    if (this.hasTitleTarget) this.titleTarget.textContent = title || ""
    if (this.hasChannelTarget) {
      this.channelTarget.textContent = channel || ""
      this.channelTarget.classList.toggle("hidden", !channel)
    }
  }

  showSheet() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    document.body.style.overflow = "hidden"
    // Reveal at the start state (panel below the fold), paint it, then slide up.
    this.sheetTarget.classList.remove("hidden", "pointer-events-none")
    this.sheetTarget.classList.add("flex")
    void this.panelTarget.offsetHeight // force a reflow so the slide animates
    this.panelTarget.classList.remove("translate-y-full")
    this.openValue = true
  }

  hideSheet() {
    this.panelTarget.classList.add("translate-y-full")
    this.sheetTarget.classList.add("pointer-events-none")
    document.body.style.overflow = ""
    this.openValue = false
    // After the slide-down, fully hide the layer and clear the player so the
    // iframe stops and audio dies. Must match the panel transition duration.
    if (this.hideTimeout) clearTimeout(this.hideTimeout)
    this.hideTimeout = setTimeout(() => {
      this.hideTimeout = null
      this.sheetTarget.classList.add("hidden")
      this.sheetTarget.classList.remove("flex")
      this.contentTarget.replaceChildren()
    }, 500)
  }

  // Synchronous, animation-free teardown for turbo:before-cache.
  forceClose() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.panelTarget.classList.add("translate-y-full")
    this.sheetTarget.classList.add("hidden", "pointer-events-none")
    this.sheetTarget.classList.remove("flex")
    this.contentTarget.replaceChildren()
    document.body.style.overflow = ""
    this.openValue = false
    this.pushed = false
  }

  // Push a history entry WITHOUT changing the URL, purely so the hardware back
  // button closes the drawer (see video_popover_controller for why we don't
  // pushState a real URL).
  pushHistoryEntry() {
    history.pushState({ revealPlayer: true }, "")
    this.pushed = true
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.openValue) return

    if (this.pushed) {
      this.pushed = false
      history.back() // popstate → handlePopState closes the sheet
    } else {
      this.hideSheet()
    }
  }

  backdropClick(event) {
    if (event.target === event.currentTarget) this.close(event)
  }

  handlePopState() {
    if (!this.openValue) return
    this.pushed = false
    this.hideSheet()
  }
}
